<template>
  <div>
    <Page404 v-if="isDocomoPlan" />
    <section v-else class="p-box">
      <ValidationObserver ref="form">
        <form>
          <div class="p-box__body">
            <Loading v-show="loading" />
            <template v-if="!loading">
              <template v-if="brands.length === 0">
                {{ $t('messages.cannotAddStoreDueToNoBrands') }}
                <div class="c-button c-button--right">
                  <router-link class="c-button__item c-button__item--primary" :to="{ name: 'CompaniesManagement', params: { company_uuid: companyUuid }}">
                    {{ $t('labels.add', { value: $t('models.brand.modelName') }) }}
                  </router-link>
                </div>
              </template>
              <template v-else>
                <div class="c-grid">
                  <div class="c-grid__items">
                    <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__6 c-grid__item__l__6">
                      <div class="p-formGrid">
                        <div class="p-formGrid__items">
                          <fieldset v-if="inboundOption" class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <KkToggleSwitch
                              id="enableMultiLang"
                              :label="$t('pages.CompaniesLocationStoreEdit.multiLang.enableMultiLang')"
                              :model-value="enableMultiLang"
                              @update:model-value="value => enableMultiLang = value"
                            />
                          </fieldset>
                          <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <label class="c-formLabel">
                              <p>{{ formatLabelText({ label: 'models.store.name', required: true }) }}</p>
                            </label>
                            <ValidationProvider key="store_name_validation" v-slot="{ errors, value, handleChange }" v-model="store.name" :name="$t('models.store.name')" rules="required">
                              <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                              <ValidationError :error="errors[0]" />
                            </ValidationProvider>
                          </fieldset>
                          <fieldset v-if="inboundOption && enableMultiLang" class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <div class="multiLang">
                              <p class="multiLang__status">
                                <StatusBadge :status="extractStatusNumber" />
                                {{ $t(`pages.CompaniesLocationStoreEdit.multiLang.${existMultiLang ? 'setMultiLang' : 'notSetMultiLang'}`) }}
                              </p>
                              <label class="c-formLabel">
                                <p>{{ formatLabelText({ label: 'pages.CompaniesLocationStoreEdit.multiLang.label.english', required: false }) }}</p>
                              </label>
                              <ValidationProvider v-slot="{ errors, value, handleChange }"
                                                  key="en_name"
                                                  v-model="storeMultiLanguage.enName"
                                                  :name="$t('pages.CompaniesLocationStoreEdit.multiLang.label.english')"
                                                  rules="max:140"
                              >
                                <input :value="value" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                                <ValidationError :error="errors[0]" />
                              </ValidationProvider>
                              <label class="c-formLabel">
                                <p>{{ formatLabelText({ label: 'pages.CompaniesLocationStoreEdit.multiLang.label.traditionalChinese', required: false }) }}</p>
                              </label>
                              <ValidationProvider v-slot="{ errors, value, handleChange }"
                                                  key="zh_tw_name"
                                                  v-model="storeMultiLanguage.zhTwName"
                                                  :name="$t('pages.CompaniesLocationStoreEdit.multiLang.label.traditionalChinese')"
                                                  rules="max:140"
                              >
                                <input :value="value" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                                <ValidationError :error="errors[0]" />
                              </ValidationProvider>
                              <label class="c-formLabel">
                                <p>{{ formatLabelText({ label: 'pages.CompaniesLocationStoreEdit.multiLang.label.korean', required: false }) }}</p>
                              </label>
                              <ValidationProvider v-slot="{ errors, value, handleChange }"
                                                  key="ko_name"
                                                  v-model="storeMultiLanguage.koName"
                                                  :name="$t('pages.CompaniesLocationStoreEdit.multiLang.label.korean')"
                                                  rules="max:140"
                              >
                                <input :value="value" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                                <ValidationError :error="errors[0]" />
                              </ValidationProvider>
                            </div>
                            <KkInfo>
                              {{ $t('pages.CompaniesLocationStoreEdit.multiLang.notice') }}
                            </KkInfo>
                          </fieldset>
                          <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <label class="c-formLabel">
                              <p>{{ formatLabelText({ label: 'models.store.brand', required: true }) }}</p>
                            </label>
                            <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="store.brand_id" :name="$t('models.brand.modelName')" rules="required">
                              <div class="c-select">
                                <select :value="value" @change="handleChange">
                                  <option v-for="brand in brands" :key="brand.id" :value="brand.id">
                                    {{ brand.attributes.name }}
                                  </option>
                                </select>
                              </div>
                              <ValidationError :error="errors[0]" />
                            </ValidationProvider>
                          </fieldset>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </div>
          <template v-if="!loading && brands.length !== 0">
            <footer class="p-box__footer">
              <div class="c-button c-button--left">
                <template v-if="canBulkRegistration">
                  <router-link
                    :to="hasGmbAccount ? { name: 'CompaniesGMBLinkageBrand', params: { company_uuid: companyUuid }} : '#'"
                    class="c-button__item c-button__item--outline"
                    :disabled="!hasGmbAccount"
                    :class="{'c-button__item--disabled': !hasGmbAccount}"
                  >
                    {{ $t('pages.CompaniesLocationStoreBulkRegistration.title') }}
                  </router-link>
                  <Info v-if="!hasGmbAccount">
                    <div class="c-info__content">
                      {{ $t('pages.CompaniesLocationStoreNew.bulkRegistration.description1') }}
                      <router-link :to="{ name: 'CompaniesGoogleOauth', params: { company_uuid: companyUuid }}" target="_blank">{{ $t('labels.here') }}</router-link>
                      {{ $t('pages.CompaniesLocationStoreNew.bulkRegistration.description2') }}
                    </div>
                  </Info>
                </template>
              </div>
              <div class="c-button c-button--right">
                <router-link :to="{ name: 'CompaniesLocationStores', params: { company_uuid: companyUuid }}" class="c-button__item c-button__item--outline-cancel">
                  {{ $t('labels.cancel') }}
                </router-link>
                <button class="c-button__item c-button__item--primary" @click.prevent="onSubmit">
                  {{ $t('labels.save') }}
                </button>
              </div>
            </footer>
          </template>
        </form>
      </ValidationObserver>
    </section>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import Loading from '../components/Loading.vue'
import ValidationError from '../components/ValidationError'
import Info from '../components/Info'
import formatLabelText from '../modules/formatLabelText'
import { KkToggleSwitch } from '../components/base'
import { KkInfo } from '../components/ui'
import StatusBadge from '../components/StatusBadge'
import Page404 from '../views/Page404'

export default {
  name: 'CompaniesStore',
  components: {
    Loading,
    ValidationError,
    Info,
    KkToggleSwitch,
    KkInfo,
    StatusBadge,
    Page404
  },
  data () {
    return {
      companyUuid: this.$route.params.company_uuid,
      enableMultiLang: false,
      storeMultiLanguage: {
        enName: '',
        zhTwName: '',
        koName: ''
      }
    }
  },
  computed: {
    ...mapState({
      company: state => state.companiesCompany.data,
      brands: state => state.companiesBrands.data,
      brands_loading: state => state.companiesBrands.loading,
      store: state => state.companiesLocationStore.data,
      store_loading: state => state.companiesLocationStore.loading,
      store_company: state => state.companiesCompany.data,
      store_company_loading: state => state.companiesCompany.loading,
      gmb_locations_loading: state => state.companiesStoreGmbLocations.loading,
    }),
    ...mapGetters({
      inboundOption: 'companiesCompany/inboundOption',
      directSelling: 'companiesCompany/directSelling',
      isRettyDistributer: 'companiesCompany/isRettyDistributer',
      isDocomoPlan: 'companiesCompany/isDocomoPlan',
      isAdmin: 'companiesAccessibleApi/isAdmin',
      isDistributer: 'companiesAccessibleApi/isDistributer',
    }),
    loading: function() {
      return this.brands_loading || this.store_company_loading || this.gmb_locations_loading || this.store_loading
    },
    hasGmbAccount: function() {
      return !!this.store_company?.attributes?.has_gmb_account
    },
    existMultiLang() {
      return !!(this.storeMultiLanguage.enName || this.storeMultiLanguage.zhTwName || this.storeMultiLanguage.koName)
    },
    extractStatusNumber() {
      const REGISTERED = 1
      const NOT_ENTERED = 2
      return this.existMultiLang ? REGISTERED : NOT_ENTERED
    },
    canBulkRegistration() {
      return this.isAdmin || this.isDistributer || (!this.directSelling && !this.isRettyDistributer)
    },
  },
  created() {
    this.initNewStore()
    if (this.company.can_add_store) {
      this.showError(this.$t('messages.cannotAddStoreDueToLackOfContractStores'))
      this.$router.push({ name: 'CompaniesLocationStores', params: { company_uuid: this.companyUuid } })
    }
    this.readBrands(this.companyUuid)
  },
  methods: {
    ...mapActions({
      initNewStore: 'companiesLocationStore/initNewStore',
      createStore: 'companiesLocationStore/createStore',
      readBrands: 'companiesBrands/readBrands',
      readCompany: 'companiesCompany/readCompany',
    }),
    formatLabelText,
    showMessage: function (val) {
      this.$updateFlash({ type: 'notice', message: val })
    },
    showError: function (val) {
      this.$updateFlash({ type: 'alert', message: val })
    },
    onSubmit: async function () {
      const result = await this.$refs.form.validate()
      if (!result.valid) {
        alert(this.$t('messages.invalid'))
        return
      }
      const payload = {
        store: {
          name: this.store.name,
          brand_id: this.store.brand_id
        },
        store_gmb: {
          location: this.store.location?.id ?? null
        }
      }
      if(this.enableMultiLang && this.existMultiLang) payload['store_multi_language'] = {
        en_name: this.storeMultiLanguage.enName,
        zh_tw_name: this.storeMultiLanguage.zhTwName,
        ko_name: this.storeMultiLanguage.koName
      }
      const res = await this.createStore({ company_uuid: this.companyUuid, payload })
      if (res?.data?.attributes?.uuid) {
        this.showMessage(this.$t('messages.created', { value: this.$t('models.store.modelName') }))
        this.$router.push({ name: 'CompaniesLocationStore', params: { company_uuid: this.companyUuid, store_uuid: res?.data?.attributes?.uuid } })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.p-formGrid {
  overflow: visible;
}
.p-box__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.c-button {
  margin: 0;
}
.c-button--right{
  margin: 0;
}
.c-button__item {
  margin-bottom: 0;
}
.multiLang {
  background-color: #F4F4F4;
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 4px;
  &__status {
    font-size: 12px;
  }
  .c-formLabel{
    margin-top: 24px;
  }
}
</style>
